// InstrumentSearch — Typeahead search modal (Angel One light theme)
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTradingWatchlist } from '../../../hooks/useWatchlist';
import { useTrading } from '../../../context/TradingContext';
import angelOneApi from '../../../services/angelOneApi';
import { searchInstruments } from '../../../services/instrumentMaster';
import { FaSearch, FaTimes, FaPlus } from 'react-icons/fa';

export default function InstrumentSearch({ onClose }) {
  const { setOrderModal } = useTrading();
  const { addToWatchlist } = useTradingWatchlist();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);

    try {
      const localResults = await searchInstruments(q);
      if (localResults.length > 0) {
        setResults(localResults);
        setLoading(false);
        return;
      }

      const result = await angelOneApi.searchScrip({ exchange: '', searchscrip: q });
      if (result.data?.data) {
        setResults(result.data.data);
      }
    } catch (e) {
      console.error('Search failed:', e);
    }
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-start justify-center pt-20 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <FaSearch className="text-gray-400 text-sm" />
          <input
            ref={inputRef}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search stocks, futures, options..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
          {query && (
            <button onClick={() => { setQuery(''); setResults([]); }} className="text-gray-400 hover:text-gray-600">
              <FaTimes className="text-xs" />
            </button>
          )}
          <kbd className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono border border-gray-200">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="p-4 text-center">
              <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
            </div>
          )}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="p-4 text-center text-xs text-gray-400">No results found</div>
          )}
          {!loading && results.length === 0 && query.length < 2 && (
            <div className="p-4 text-center text-xs text-gray-400">Type at least 2 characters to search</div>
          )}
          {results.map((inst, i) => (
            <div
              key={`${inst.token || inst.symboltoken}_${i}`}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 group cursor-pointer"
            >
              <div>
                <p className="text-xs font-semibold text-gray-900">{inst.symbol || inst.tradingsymbol}</p>
                <p className="text-[10px] text-gray-400">{inst.exch_seg || inst.exchange} — {inst.name || inst.symbol}</p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => addToWatchlist({
                    token: inst.token || inst.symboltoken,
                    symbol: inst.symbol || inst.tradingsymbol,
                    name: inst.name || inst.symbol,
                    exchange: inst.exch_seg || inst.exchange,
                  })}
                  className="p-1.5 rounded text-blue-600 hover:bg-blue-50"
                  title="Add to watchlist"
                >
                  <FaPlus className="text-[10px]" />
                </button>
                <button
                  onClick={() => {
                    setOrderModal({
                      instrument: {
                        token: inst.token || inst.symboltoken,
                        symbol: inst.symbol || inst.tradingsymbol,
                        exchange: inst.exch_seg || inst.exchange,
                      },
                      side: 'BUY'
                    });
                    onClose();
                  }}
                  className="px-2.5 py-1 rounded text-[10px] font-bold bg-green-50 text-green-600 hover:bg-green-100 border border-green-200"
                >
                  BUY
                </button>
                <button
                  onClick={() => {
                    setOrderModal({
                      instrument: {
                        token: inst.token || inst.symboltoken,
                        symbol: inst.symbol || inst.tradingsymbol,
                        exchange: inst.exch_seg || inst.exchange,
                      },
                      side: 'SELL'
                    });
                    onClose();
                  }}
                  className="px-2.5 py-1 rounded text-[10px] font-bold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                >
                  SELL
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
